import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { v4 as uuidv4 } from 'uuid';
import { testTemplateAPI } from '../../api/testTemplateApi';
import { testDataAPI } from '../../api/testDataApi';
import ChipSelect from '../../components/Portal/ChipSelect';
import * as S from './TestTemplate.styles';

const QUESTION_TYPE_OPTIONS = [
  { value: 'Objective', label: 'Objective' },
  { value: 'Subjective', label: 'Subjective' },
];

const TEMPLATE_TYPE_OPTIONS = [
  { value: 'mockTest', label: 'Mock Test' },
  { value: 'finalExam', label: 'Final Exam' },
];

const TERM_OPTIONS = [1, 2, 3, 4].map(term => ({ value: term, label: `Term ${term}` }));

const TestTemplate = ({ backPath }) => {  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const dashboardPath = backPath || '/dashboard';
  const [templateId, setTemplateId] = useState(searchParams.get('id') || uuidv4());
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isExistingTemplate, setIsExistingTemplate] = useState(false);  // 模板类型状态 - 从URL参数获取或默认为finalExam
  const [templateType, setTemplateType] = useState(() => {
    const typeFromUrl = searchParams.get('type');
    return typeFromUrl === 'mockTest' ? 'mockTest' : 'finalExam';
  }); // 'mockTest' or 'finalExam'
    // Add question type selection state
  const [selectedQuestionType, setSelectedQuestionType] = useState({});
  
  // Add section collapse state
  const [collapsedSections, setCollapsedSections] = useState({});
  
  // Track questions that were loaded from server (not newly added)
  const [serverLoadedQuestions, setServerLoadedQuestions] = useState(new Set());
  
  // Add navigation state
  const [showNavigation, setShowNavigation] = useState(true);
    // Add batch add question count state
  const [batchAddCount, setBatchAddCount] = useState({});
    // Add batch add question score state
  const [batchAddScore, setBatchAddScore] = useState({});

  // Add section management state for Final Exam
  const [newSectionName, setNewSectionName] = useState('');
  const [showAddSection, setShowAddSection] = useState(false);

  // Add section editing state for Final Exam
  const [editingSectionName, setEditingSectionName] = useState(null);
  const [editingSectionNewName, setEditingSectionNewName] = useState('');
  const [bulkUpdateProgress, setBulkUpdateProgress] = useState(null);

  // Add state to track invalid questions (for visual feedback)
  const [invalidQuestions, setInvalidQuestions] = useState({});

  // Add state for template term (1, 2, 3, or 4)
  const [templateTerm, setTemplateTerm] = useState(null);

  // Custom assets state (cover image and exam advice PDF)
  const [coverImageUrl, setCoverImageUrl] = useState(null);
  const [examAdvicePdfUrl, setExamAdvicePdfUrl] = useState(null);
  const [isUploadingCover, setIsUploadingCover] = useState(false);
  const [isUploadingAdvicePdf, setIsUploadingAdvicePdf] = useState(false);

  // Mock Test 模板的初始数据结构
  const mockTestInitialData = {
    examName: '',
    examDate: '',
    sections: {
      QR: {
        questionCount: 0,
        questions: [],
        difficulty: 1,
        sectionAdvice: '',
        sectionAdviceCN: ''
      },
      AR: {
        questionCount: 0,
        questions: [],
        difficulty: 1,
        sectionAdvice: '',
        sectionAdviceCN: ''
      },
      Reading: {
        questionCount: 0,
        questions: [],
        difficulty: 1,
        sectionAdvice: '',
        sectionAdviceCN: ''
      },
      Writing: {
        questionCount: 0,
        questions: [],
        difficulty: 1,
        sectionAdvice: '',
        sectionAdviceCN: ''
      }
    },
    examAdvice: '',
    examAdviceCN: ''
  };  // Final Exam 模板的初始数据结构 - 现在为空的sections，允许用户自定义
  const finalExamInitialData = {
    examName: '',
    examDate: '',
    sections: {},
    examAdvice: '',
    examAdviceCN: ''
  };

  // 默认的Final Exam sections（用户可以选择添加）
  const defaultFinalExamSections = [
    'Math Arithmetic',
    'Math Problem solving', 
    'Language Conventions',
    'Reading',
    'Writing',
    'General Ability'
  ];
  // 初始化模板数据 - 根据模板类型初始化
  const [templateData, setTemplateData] = useState(() => {
    const typeFromUrl = searchParams.get('type');
    return typeFromUrl === 'mockTest' ? mockTestInitialData : finalExamInitialData;
  });

  // 权限检查
  const userGroups = auth.user?.profile['cognito:groups'] || [];
  const isAdmin = userGroups.includes('site_admin') || userGroups.includes('portal_developer') || userGroups.includes('portal_system_admin') || userGroups.includes('portal_director') || userGroups.includes('portal_manager') || userGroups.includes('portal_admin');
  const isTeacher = userGroups.includes('teacher') || userGroups.includes('portal_teacher');
  const hasPermission = isAdmin || isTeacher;  // 页面初始化时滚动到顶部
  useEffect(() => {
    // 页面加载完成后滚动到顶部
    window.scrollTo(0, 0);
  }, []);

  // 加载模板数据
  useEffect(() => {
    const loadTemplate = async () => {
      // 检查是否是克隆操作
      const cloneFromId = searchParams.get('cloneFrom');
      if (cloneFromId && auth.isAuthenticated) {
        try {
          // 获取要克隆的模板
          const originalTemplate = await testTemplateAPI.getTemplate(cloneFromId, auth.user?.id_token);

          // 修改模板数据用于克隆
          const clonedTemplateData = {
            ...originalTemplate.templateData,
            examName: `${originalTemplate.templateData.examName} (Copy)`,
            examDate: '', // 重置考试日期
          };

          setTemplateData(clonedTemplateData);
          setTemplateType(originalTemplate.templateData?.templateType || 'mockTest');
          setTemplateId(uuidv4()); // 生成新的ID
          setIsExistingTemplate(false); // 设置为新模板
          return; // 早期返回，不执行后续的加载逻辑
        } catch (error) {
          console.error('Error loading template for cloning:', error);
          alert('Error loading template to clone. Starting with empty template.');
        }
      }

      const urlTemplateId = searchParams.get('id');
      if (urlTemplateId && auth.isAuthenticated) {
        setIsLoading(true);
        try {
          const template = await testTemplateAPI.getTemplate(urlTemplateId, auth.user?.id_token);
          setTemplateData(template.templateData);
          setTemplateTerm(template.term || null);  // Load term from template
          setCoverImageUrl(template.coverImageUrl || null);  // Load custom cover image URL
          setExamAdvicePdfUrl(template.examAdvicePdfUrl || null);  // Load custom exam advice PDF URL
          setIsExistingTemplate(true);
          setTemplateId(urlTemplateId); // 确保 state 与 URL 同步
          
          // Track all questions loaded from server
          const loadedQuestionIds = new Set();
          Object.entries(template.templateData.sections).forEach(([sectionType, section]) => {
            section.questions.forEach((question, index) => {
              loadedQuestionIds.add(`${sectionType}-${index}`);
            });
          });
          setServerLoadedQuestions(loadedQuestionIds);          // 根据模板数据判断模板类型 - 检查templateType字段或根据sections推断
          if (template.templateData.templateType) {
            setTemplateType(template.templateData.templateType);
          } else {
            // 向后兼容：根据sections判断模板类型
            const sections = template.templateData.sections;
            const sectionNames = Object.keys(sections);
            const mockTestSections = ['QR', 'AR', 'Reading', 'Writing'];
            
            // 如果有QR和AR sections，并且正好是4个预期的sections，则判断为mockTest
            if (sectionNames.includes('QR') && sectionNames.includes('AR') && 
                sectionNames.length === 4 && 
                mockTestSections.every(section => sectionNames.includes(section))) {
              setTemplateType('mockTest');
            } else {
              setTemplateType('finalExam');
            }
          }
        } catch (error) {
          console.error('Error loading template:', error);
          alert('Error loading template. Starting with empty template.');
        } finally {
          setIsLoading(false);
        }
      }
    };

    loadTemplate();
  }, [auth.isAuthenticated, auth.user?.id_token, searchParams]);

  // 清理无效题目状态当模板数据改变时
  useEffect(() => {
    // 清理不存在的题目的无效状态
    setInvalidQuestions(prev => {
      const newInvalid = {};
      Object.keys(prev).forEach(key => {
        const [sectionType, index] = key.split('-');
        const section = templateData.sections[sectionType];
        if (section && section.questions[parseInt(index)]) {
          const question = section.questions[parseInt(index)];
          const score = question.score;
          if (score === '' || score === null || score === undefined || 
              isNaN(score) || score <= 0) {
            newInvalid[key] = true;
          }
        }
      });
      return newInvalid;
    });
  }, [templateData.sections]);

  // 如果未登录或没有权限，重定向
  if (!auth.isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!hasPermission) {
    return (
      <S.Container>
        <S.Header>
          <S.Title>Access Denied</S.Title>
        </S.Header>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <h2>You don't have permission to access this page</h2>
          <p>Only site administrators and teachers can create or edit test templates.</p>
          <S.Button onClick={() => navigate(dashboardPath)}>Back to Dashboard</S.Button>
        </div>
      </S.Container>
    );
  }  // 创建新题目的模板
  const createNewQuestion = (sectionType, questionType = 'Objective', questionNumber = '', score = 1) => {
    const baseQuestion = {
      sectionName: sectionType,
      questionNumber: questionNumber, // 自动生成或用户指定的题号
      questionName: '',
      questionDescription: '', // 可选的题目描述
      score: score, // 使用传入的分数
      questionType: questionType // 'Objective' or 'Subjective'
    };

    // Both Mock Test and Final Exam now use the same unified structure
    return baseQuestion;
  };  // 添加题目
  const addQuestion = (sectionType, questionType = 'Objective', customScore = null) => {
    setTemplateData(prev => {
      const section = prev.sections[sectionType];
      
      // 自动生成题号：找到当前section中最大的题号，然后+1
      const existingNumbers = section.questions
        .map(q => {
          const num = parseInt(q.questionNumber);
          return isNaN(num) ? 0 : num;
        })
        .filter(num => num > 0);
      
      const nextQuestionNumber = existingNumbers.length > 0 
        ? Math.max(...existingNumbers) + 1 
        : 1;
      
      // 使用自定义分数或默认分数
      const questionScore = customScore !== null ? customScore : 1;
      
      const newQuestion = createNewQuestion(sectionType, questionType, nextQuestionNumber.toString(), questionScore);
      
      return {
        ...prev,
        sections: {
          ...prev.sections,
          [sectionType]: {
            ...section,
            questions: [...section.questions, newQuestion],
            questionCount: section.questions.length + 1
          }
        }
      };
    });
  };
  // 删除题目
  const removeQuestion = (sectionType, questionIndex) => {
    setTemplateData(prev => {
      const section = prev.sections[sectionType];
      const updatedQuestions = section.questions.filter((_, index) => index !== questionIndex);

      return {
        ...prev,
        sections: {
          ...prev.sections,
          [sectionType]: {
            ...section,
            questions: updatedQuestions,
            questionCount: updatedQuestions.length
          }
        }
      };
    });

    // Update server loaded questions tracking
    setServerLoadedQuestions(prev => {
      const newSet = new Set(prev);
      // Remove the deleted question
      newSet.delete(`${sectionType}-${questionIndex}`);
      // Update indices for questions after the deleted one
      const updatedSet = new Set();
      newSet.forEach(id => {
        const [idSection, idIndex] = id.split('-');
        const index = parseInt(idIndex);
        if (idSection === sectionType && index > questionIndex) {
          updatedSet.add(`${idSection}-${index - 1}`);
        } else if (idSection !== sectionType || index < questionIndex) {
          updatedSet.add(id);
        }
      });
      return updatedSet;
    });
  };

  // 向上移动题目
  const moveQuestionUp = (sectionType, questionIndex) => {
    if (questionIndex === 0) return; // 已经是第一个题目

    setTemplateData(prev => {
      const section = prev.sections[sectionType];
      const updatedQuestions = [...section.questions];

      // 交换位置
      [updatedQuestions[questionIndex - 1], updatedQuestions[questionIndex]] =
      [updatedQuestions[questionIndex], updatedQuestions[questionIndex - 1]];

      return {
        ...prev,
        sections: {
          ...prev.sections,
          [sectionType]: {
            ...section,
            questions: updatedQuestions
          }
        }
      };
    });
  };

  // 向下移动题目
  const moveQuestionDown = (sectionType, questionIndex) => {
    setTemplateData(prev => {
      const section = prev.sections[sectionType];
      if (questionIndex >= section.questions.length - 1) return prev; // 已经是最后一个题目

      const updatedQuestions = [...section.questions];

      // 交换位置
      [updatedQuestions[questionIndex], updatedQuestions[questionIndex + 1]] =
      [updatedQuestions[questionIndex + 1], updatedQuestions[questionIndex]];

      return {
        ...prev,
        sections: {
          ...prev.sections,
          [sectionType]: {
            ...section,
            questions: updatedQuestions
          }
        }
      };
    });
  };

  // 向上移动 section（仅适用于 Final Exam）
  const moveSectionUp = (sectionName) => {
    setTemplateData(prev => {
      const entries = Object.entries(prev.sections);
      const index = entries.findIndex(([name]) => name === sectionName);
      if (index <= 0) return prev;

      [entries[index - 1], entries[index]] = [entries[index], entries[index - 1]];

      return {
        ...prev,
        sections: Object.fromEntries(entries)
      };
    });
  };

  // 向下移动 section（仅适用于 Final Exam）
  const moveSectionDown = (sectionName) => {
    setTemplateData(prev => {
      const entries = Object.entries(prev.sections);
      const index = entries.findIndex(([name]) => name === sectionName);
      if (index === -1 || index >= entries.length - 1) return prev;

      [entries[index], entries[index + 1]] = [entries[index + 1], entries[index]];

      return {
        ...prev,
        sections: Object.fromEntries(entries)
      };
    });
  };

  // 清除section所有题目
  const clearSectionQuestions = (sectionType) => {
    if (!window.confirm(`Are you sure you want to clear all questions in ${sectionType} section? This action cannot be undone.`)) {
      return;
    }
    
    setTemplateData(prev => ({
      ...prev,
      sections: {
        ...prev.sections,
        [sectionType]: {
          ...prev.sections[sectionType],
          questions: [],
          questionCount: 0
        }
      }
    }));
    
    // Clear server loaded questions for this section
    setServerLoadedQuestions(prev => {
      const newSet = new Set();
      prev.forEach(id => {
        const [idSection] = id.split('-');
        if (idSection !== sectionType) {
          newSet.add(id);
        }
      });
      return newSet;
    });
  };

  // 更新基本信息
  const updateBasicInfo = (field, value) => {
    setTemplateData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // 更新部分信息
  const updateSection = (sectionType, field, value) => {
    setTemplateData(prev => ({
      ...prev,
      sections: {
        ...prev.sections,
        [sectionType]: {
          ...prev.sections[sectionType],
          [field]: value
        }
      }
    }));
  };

  // 更新题目信息
  const updateQuestion = (sectionType, questionIndex, field, value) => {
    setTemplateData(prev => {
      const section = prev.sections[sectionType];
      const updatedQuestions = section.questions.map((q, index) => {
        if (index === questionIndex) {
          if (field.startsWith('meta.')) {
            const metaField = field.replace('meta.', '');
            return {
              ...q,
              meta: {
                ...q.meta,
                [metaField]: value
              }
            };
          }
          return {
            ...q,
            [field]: value
          };
        }
        return q;
      });

      return {
        ...prev,
        sections: {
          ...prev.sections,
          [sectionType]: {
            ...section,
            questions: updatedQuestions
          }
        }
      };    });
  };

  // 添加新 section (仅适用于 Final Exam)
  const addSection = (sectionName) => {
    if (!sectionName.trim()) {
      alert('Please enter a section name.');
      return;
    }
    
    if (templateData.sections[sectionName]) {
      alert('A section with this name already exists.');
      return;
    }
    
    setTemplateData(prev => ({
      ...prev,
      sections: {
        ...prev.sections,
        [sectionName]: {
          questionCount: 0,
          questions: [],
          sectionAdvice: '',
          sectionAdviceCN: ''
        }
      }
    }));
    
    setNewSectionName('');
    setShowAddSection(false);
  };

  // 删除 section (仅适用于 Final Exam)
  const removeSection = (sectionName) => {
    if (!window.confirm(`Are you sure you want to delete the "${sectionName}" section? This will remove all questions in this section and cannot be undone.`)) {
      return;
    }
    
    setTemplateData(prev => {
      const { [sectionName]: removedSection, ...remainingSections } = prev.sections;
      return {
        ...prev,
        sections: remainingSections
      };
    });
    
    // Clear server loaded questions for the removed section
    setServerLoadedQuestions(prev => {
      const newSet = new Set();
      prev.forEach(id => {
        const [idSection] = id.split('-');
        if (idSection !== sectionName) {
          newSet.add(id);
        }
      });
      return newSet;
    });
  };

  // 从预设选项添加 section
  const addPresetSection = (sectionName) => {
    addSection(sectionName);
  };

  // 开始编辑 section 名称 (仅适用于 Final Exam)
  const startEditingSectionName = (currentSectionName) => {
    setEditingSectionName(currentSectionName);
    setEditingSectionNewName(currentSectionName);
  };

  // 取消编辑 section 名称
  const cancelEditingSectionName = () => {
    setEditingSectionName(null);
    setEditingSectionNewName('');
  };

  // 保存 section 名称更改
  const saveSectionNameChange = async () => {
    const oldName = editingSectionName;
    const newName = editingSectionNewName.trim();

    // 验证新名称
    if (!newName) {
      alert('Please enter a section name.');
      return;
    }

    if (newName === oldName) {
      cancelEditingSectionName();
      return;
    }

    if (templateData.sections[newName]) {
      alert('A section with this name already exists.');
      return;
    }

    // 确认更改
    let confirmMessage;
    if (isExistingTemplate) {
      confirmMessage = `Are you sure you want to rename "${oldName}" to "${newName}"?\n\nThis will update ALL student responses for this template. This operation cannot be undone.`;
    } else {
      confirmMessage = `Are you sure you want to rename "${oldName}" to "${newName}"?`;
    }
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    // --- Logic for New (Unsaved) Template ---
    if (!isExistingTemplate) {
      setTemplateData(prev => {
        const updatedSections = { ...prev.sections };
        const sectionToUpdate = { ...updatedSections[oldName] };

        // Update the sectionName within each question
        sectionToUpdate.questions = sectionToUpdate.questions.map(q => ({
          ...q,
          sectionName: newName
        }));

        // Create the new section entry and delete the old one
        updatedSections[newName] = sectionToUpdate;
        delete updatedSections[oldName];

        return { ...prev, sections: updatedSections };
      });

      alert(`Section renamed locally from "${oldName}" to "${newName}".`);
      cancelEditingSectionName();
      return; // End execution for new templates
    }

    // --- Logic for Existing (Saved) Template ---
    setIsLoading(true);
    const originalSections = { ...templateData.sections }; // For rollback

    try {
      // Step 1: Update all student responses first
      await updateAllResponsesForSectionNameChange(oldName, newName);

      // Step 2: If response update is successful, update the template data locally
      const updatedSections = { ...templateData.sections };
      const sectionToUpdate = { ...updatedSections[oldName] };
      sectionToUpdate.questions = sectionToUpdate.questions.map(q => ({
        ...q,
        sectionName: newName
      }));
      updatedSections[newName] = sectionToUpdate;
      delete updatedSections[oldName];

      // This state update is crucial for saveTemplateInternal to have the latest data
      setTemplateData(prev => ({
        ...prev,
        sections: updatedSections
      }));
      
      // Update server loaded questions tracking for renamed section
      setServerLoadedQuestions(prev => {
        const newSet = new Set();
        prev.forEach(id => {
          const [idSection, idIndex] = id.split('-');
          if (idSection === oldName) {
            newSet.add(`${newName}-${idIndex}`);
          } else {
            newSet.add(id);
          }
        });
        return newSet;
      });

      // Step 3: Save the updated template to the backend
      // We need to wait for the state to update, so we pass the new data directly
      // Or better, use a useEffect or a callback, but for simplicity, we'll use the updated data.
      // The `saveTemplateInternal` function will use the `templateData` from state, which we just set.
      // Let's ensure it's updated before calling save.
      
      // Awaiting a state update is not direct, so we create the final object to save.
      const finalTemplateData = {
          ...templateData,
          sections: updatedSections,
          templateType: templateType
      };

      await testTemplateAPI.updateTemplate(
        templateId,
        finalTemplateData.examName,
        finalTemplateData,
        auth.user?.id_token,
        templateType,
        templateTerm
      );

      alert(`Section renamed successfully from "${oldName}" to "${newName}".\n\nTemplate and all student responses have been updated.`);
      cancelEditingSectionName();

    } catch (error) {
      console.error('Failed to update section name and associated data:', error);
      // Rollback local template changes on any failure
      setTemplateData(prev => ({
        ...prev,
        sections: originalSections
      }));
      alert(`Failed to update section name: ${error.message}\n\nYour local changes have been reverted.`);
    } finally {
      setIsLoading(false);
      setBulkUpdateProgress(null);
    }
  };

  // 批量更新所有响应中的 section 名称
  const updateAllResponsesForSectionNameChange = async (oldSectionName, newSectionName) => {
    try {
      setBulkUpdateProgress({ phase: 'preparing', current: 0, total: 0 });

      // First, get count of responses to update
      const existingResponses = await testDataAPI.getTestDataByTemplateId(templateId, auth.user?.id_token);
      const totalResponses = existingResponses.length;
      
      setBulkUpdateProgress({ phase: 'updating', current: 0, total: totalResponses });

      if (totalResponses === 0) {
        setBulkUpdateProgress({ phase: 'completed', current: 0, total: 0 });
        setTimeout(() => {
          setBulkUpdateProgress(null);
        }, 2000);
        return;
      }

      // Perform the bulk update
      const result = await testDataAPI.updateSectionNames(
        templateId, 
        oldSectionName, 
        newSectionName, 
        auth.user?.id_token
      );
      
      if (result.success) {
        setBulkUpdateProgress({ 
          phase: 'completed', 
          current: result.updatedCount, 
          total: result.totalCount 
        });
        
        // Show success message for a moment, then clear
        setTimeout(() => {
          setBulkUpdateProgress(null);
        }, 3000);
      } else {
        setBulkUpdateProgress({ phase: 'failed', error: result.error || 'Bulk update failed' });
        throw new Error(result.error || 'Bulk update failed');
      }
    } catch (error) {
      setBulkUpdateProgress({ phase: 'failed', error: error.message });
      throw error;
    }
  };

  // Handle cover image upload
  const handleCoverImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      alert('Please upload a JPEG or PNG image.');
      return;
    }

    // Validate file size (5MB limit)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      alert('Image file size must be less than 5MB.');
      return;
    }

    setIsUploadingCover(true);
    try {
      // Get presigned URL
      const uploadData = await testTemplateAPI.getUploadUrl(
        templateId,
        'coverImage',
        file.type,
        auth.user?.id_token
      );

      // Upload to S3
      await testTemplateAPI.uploadFileToS3(uploadData.uploadUrl, file, file.type);

      // Update template with new cover image URL
      await testTemplateAPI.updateCustomAssets(
        templateId,
        uploadData.publicUrl,
        undefined,  // Don't change exam advice PDF
        auth.user?.id_token
      );

      // Update local state
      setCoverImageUrl(uploadData.publicUrl);
      alert('Cover image uploaded successfully!');
    } catch (error) {
      console.error('Error uploading cover image:', error);
      alert(`Failed to upload cover image: ${error.message}`);
    } finally {
      setIsUploadingCover(false);
      // Reset file input
      event.target.value = '';
    }
  };

  // Handle exam advice PDF upload
  const handleExamAdvicePdfUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (file.type !== 'application/pdf') {
      alert('Please upload a PDF file.');
      return;
    }

    // Validate file size (10MB limit)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      alert('PDF file size must be less than 10MB.');
      return;
    }

    setIsUploadingAdvicePdf(true);
    try {
      // Get presigned URL
      const uploadData = await testTemplateAPI.getUploadUrl(
        templateId,
        'examAdvicePdf',
        file.type,
        auth.user?.id_token
      );

      // Upload to S3
      await testTemplateAPI.uploadFileToS3(uploadData.uploadUrl, file, file.type);

      // Update template with new exam advice PDF URL
      await testTemplateAPI.updateCustomAssets(
        templateId,
        undefined,  // Don't change cover image
        uploadData.publicUrl,
        auth.user?.id_token
      );

      // Update local state
      setExamAdvicePdfUrl(uploadData.publicUrl);
      alert('Exam advice PDF uploaded successfully!');
    } catch (error) {
      console.error('Error uploading exam advice PDF:', error);
      alert(`Failed to upload exam advice PDF: ${error.message}`);
    } finally {
      setIsUploadingAdvicePdf(false);
      // Reset file input
      event.target.value = '';
    }
  };

  // Remove cover image
  const removeCoverImage = async () => {
    if (!window.confirm('Are you sure you want to remove the custom cover image? The default cover will be used instead.')) {
      return;
    }

    setIsLoading(true);
    try {
      await testTemplateAPI.updateCustomAssets(
        templateId,
        null,  // Remove cover image (set to null)
        undefined,  // Don't change exam advice PDF
        auth.user?.id_token
      );
      setCoverImageUrl(null);
      alert('Custom cover image removed.');
    } catch (error) {
      console.error('Error removing cover image:', error);
      alert(`Failed to remove cover image: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Remove exam advice PDF
  const removeExamAdvicePdf = async () => {
    if (!window.confirm('Are you sure you want to remove the exam advice PDF?')) {
      return;
    }

    setIsLoading(true);
    try {
      await testTemplateAPI.updateCustomAssets(
        templateId,
        undefined,  // Don't change cover image
        null,  // Remove exam advice PDF (set to null)
        auth.user?.id_token
      );
      setExamAdvicePdfUrl(null);
      alert('Exam advice PDF removed.');
    } catch (error) {
      console.error('Error removing exam advice PDF:', error);
      alert(`Failed to remove exam advice PDF: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // 保存模板
  const saveTemplate = async () => {
    if (!templateData.examName.trim()) {
      alert('Please enter an exam name before saving.');
      return;
    }

    // 验证模板结构
    const validationErrors = [];
    
    // 对于Mock Test，验证必须有4个固定的sections
    if (templateType === 'mockTest') {
      const requiredSections = ['QR', 'AR', 'Reading', 'Writing'];
      const currentSections = Object.keys(templateData.sections);
      
      // 检查是否有所有必需的sections
      requiredSections.forEach(requiredSection => {
        if (!currentSections.includes(requiredSection)) {
          validationErrors.push(`Mock Test must include ${requiredSection} section`);
        }
      });
      
      // 检查是否有额外的sections
      currentSections.forEach(currentSection => {
        if (!requiredSections.includes(currentSection)) {
          validationErrors.push(`Mock Test cannot have section: ${currentSection}`);
        }
      });
      
      // 检查sections数量
      if (currentSections.length !== 4) {
        validationErrors.push(`Mock Test must have exactly 4 sections (QR, AR, Reading, Writing). Currently has ${currentSections.length} sections.`);
      }
    }
    
    // 验证每个题目的必填字段
    Object.entries(templateData.sections).forEach(([sectionName, section]) => {
      section.questions.forEach((question, index) => {
        const questionLabel = `${sectionName} - Question ${index + 1}`;
        
        // 检查Question Number是否存在且不为空
        if (!question.questionNumber || question.questionNumber.toString().trim() === '') {
          validationErrors.push(`${questionLabel}: Question Number is required`);
        }
        
        // 检查score是否大于0（包括空值和NaN的检查）
        if (question.score === '' || question.score === null || question.score === undefined || 
            isNaN(question.score) || question.score <= 0) {
          validationErrors.push(`${questionLabel}: Score must be greater than 0`);
        }
      });
    });

    // 如果有验证错误，显示所有错误并停止保存
    if (validationErrors.length > 0) {
      const errorMessage = 'Please fix the following issues before saving:\n\n' + 
        validationErrors.join('\n');
      alert(errorMessage);
      return;
    }

    setIsLoading(true);
    try {
      // 在模板数据中包含类型信息
      const templateDataWithType = {
        ...templateData,
        templateType: templateType
      };        // 计算分数信息
        const totalScore = calculateTotalScore();
        const sectionScores = calculateSectionScores();
        const totalQuestions = Object.values(templateData.sections).reduce((sum, section) => sum + section.questionCount, 0);
        
        const scoreDetails = Object.entries(sectionScores)
          .map(([sectionName, data]) => `${sectionName}: ${data.score} points (${data.questionCount} questions)`)
          .join('\n');

        if (isExistingTemplate) {
        // 更新现有模板
        await testTemplateAPI.updateTemplate(
          templateId,
          templateData.examName,
          templateDataWithType,
          auth.user?.id_token,
          templateType,  // 显式传递模板类型
          templateTerm   // 显式传递term
        );
        alert(`Template updated successfully!\n\nTotal Score: ${totalScore}\nTotal number of questions: ${totalQuestions}\n\nSection and marks:\n${scoreDetails}`);
      } else {
        // 创建新模板
        const createdTemplate = await testTemplateAPI.createTemplate(
          templateData.examName,
          templateDataWithType,
          auth.user?.id_token,
          templateType,  // 显式传递模板类型
          templateTerm   // 显式传递term
        );
          // 更新本地状态，使用服务器返回的真实 ID
        const newTemplateId = createdTemplate.id;
        setTemplateId(newTemplateId);
        
        // 更新 URL 中的 ID，这样刷新页面时会使用正确的 ID
        const newUrl = new URL(window.location);
        newUrl.searchParams.set('id', newTemplateId);
        window.history.replaceState({}, '', newUrl);
        
        setIsExistingTemplate(true);
        alert(`Template created successfully!\n\nTotal Score: ${totalScore}分\nTotal number of questions: ${totalQuestions}\n\nSection and marks:\n${scoreDetails}`);
      }
      // Output schema to console
      console.log('Template Data Schema:', JSON.stringify(templateData, null, 2));
      console.log('Template Structure:', {
        templateType: templateType,
        sections: Object.keys(templateData.sections),
        totalQuestions: Object.values(templateData.sections).reduce((sum, section) => sum + section.questionCount, 0),
        sectionsDetails: Object.entries(templateData.sections).map(([name, section]) => ({
          sectionName: name,
          questionCount: section.questionCount,
          questions: section.questions.map(q => ({
            questionNumber: q.questionNumber,
            questionName: q.questionName,
            questionType: q.questionType,
            score: q.score,
            sectionName: q.sectionName
          }))
        }))
      });
    } catch (error) {
      console.error('Error saving template:', error);
      alert('Error saving template. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // 切换编辑模式
  const toggleEdit = () => {
    setIsEditing(!isEditing);
  };

  // 返回Dashboard
  const goBack = () => {
    navigate(dashboardPath);
  };  // 切换模板类型
  const handleTemplateTypeChange = (newType) => {
    if (isExistingTemplate) {
      alert('Cannot change template type for existing templates.');
      return;
    }
    
    // 如果当前有数据且要切换到不同类型，确认是否要重置数据
    const hasExistingData = Object.keys(templateData.sections).length > 0 || 
                           templateData.examName.trim() !== '' || 
                           templateData.examAdvice.trim() !== '';
    
    if (hasExistingData && newType !== templateType) {
      if (!window.confirm('Changing template type will reset all your current data. Are you sure you want to continue?')) {
        return;
      }
    }
    
    setTemplateType(newType);
    // Clear server loaded questions when changing template type
    setServerLoadedQuestions(new Set());
    
    if (newType === 'mockTest') {
      setTemplateData(mockTestInitialData);
    } else if (newType === 'finalExam') {
      // 为Final Exam创建带有默认sections的初始数据
      const finalExamWithDefaultSections = {
        ...finalExamInitialData,
        sections: defaultFinalExamSections.reduce((acc, sectionName) => {
          acc[sectionName] = {
            questionCount: 0,
            questions: [],
            sectionAdvice: '',
            sectionAdviceCN: ''
          };
          return acc;
        }, {})
      };
      setTemplateData(finalExamWithDefaultSections);
    }
  };const handleAddQuestion = (sectionType) => {
    const questionType = selectedQuestionType[sectionType] || 'Objective';
    const count = batchAddCount[sectionType] || 1;
    const score = batchAddScore[sectionType] !== undefined ? batchAddScore[sectionType] : 1;
    
    // 验证分数
    if (score === '' || score === null || score === undefined || isNaN(score) || score <= 0) {
      alert('Please enter a valid score greater than 0 before adding questions.');
      return;
    }
    
    // 批量添加题目
    for (let i = 0; i < count; i++) {
      addQuestion(sectionType, questionType, score);
    }
  };


  // Toggle section collapse
  const toggleSectionCollapse = (sectionType) => {
    setCollapsedSections(prev => ({
      ...prev,
      [sectionType]: !prev[sectionType]
    }));
  };  // Scroll to section with proper offset and timing
  const scrollToSection = (elementId) => {
    // 使用 setTimeout 确保 DOM 更新完成
    setTimeout(() => {
      const element = document.getElementById(elementId);
      if (element) {
        // 计算导航栏高度和其他固定元素的偏移量
        const topOffset = 100; // 预留顶部空间
        const bottomOffset = 200; // 预留底部空间
        
        // 获取元素位置和页面信息
        const elementRect = element.getBoundingClientRect();
        const absoluteElementTop = elementRect.top + window.pageYOffset;
        const windowHeight = window.innerHeight;
        const documentHeight = document.documentElement.scrollHeight;
        
        // 计算目标滚动位置
        let targetPosition = absoluteElementTop - topOffset;
        
        // 如果是接近页面底部的元素，调整滚动位置
        const maxScrollPosition = documentHeight - windowHeight;
        if (targetPosition > maxScrollPosition - bottomOffset) {
          // 对于底部元素，滚动到能看到该元素的最大位置
          targetPosition = Math.max(0, absoluteElementTop - windowHeight + elementRect.height + bottomOffset);
        }
        
        // 确保滚动位置在有效范围内
        targetPosition = Math.max(0, Math.min(targetPosition, maxScrollPosition));
        
        // 平滑滚动到目标位置
        window.scrollTo({
          top: targetPosition,
          behavior: 'smooth'
        });
      }
    }, 100); // 100ms 延迟确保 DOM 渲染完成
  };
  // 渲染题目元数据字段
  const renderQuestionMeta = (sectionType, question, questionIndex) => {
    // Check if this question was loaded from server
    const isServerLoadedQuestion = serverLoadedQuestions.has(`${sectionType}-${questionIndex}`);
    
    // Both Mock Test and Final Exam now use the same unified question structure
    return (
      <>
        <S.Row>
          <S.Col>
            <S.FormGroup>
              <S.Label>Question Number</S.Label>
              {isEditing && isAdmin && !isServerLoadedQuestion ? (
                <S.Input
                  value={question.questionNumber || ''}
                  onChange={(e) => updateQuestion(sectionType, questionIndex, 'questionNumber', e.target.value)}
                  placeholder="Enter question number..."
                />
              ) : (
                <div style={{ padding: '10px', border: '1px solid #e9ecef', borderRadius: '4px', background: '#f8f9fa' }}>
                  {question.questionNumber || 'Not specified'}
                </div>
              )}
            </S.FormGroup>
          </S.Col>
          <S.Col>
            <S.FormGroup>
              <S.Label>Question Type</S.Label>
              {isEditing && isAdmin && !isServerLoadedQuestion ? (
                <ChipSelect
                  aria-label="Question type"
                  options={QUESTION_TYPE_OPTIONS}
                  value={question.questionType || 'Objective'}
                  onChange={(questionType) => updateQuestion(sectionType, questionIndex, 'questionType', questionType)}
                />
              ) : (
                <div style={{ padding: '10px', border: '1px solid #e9ecef', borderRadius: '4px', background: '#f8f9fa' }}>
                  {question.questionType || 'Objective'}
                </div>
              )}
            </S.FormGroup>
          </S.Col>
        </S.Row>
        <S.Row>
          <S.Col>
            <S.FormGroup>
              <S.Label>Question Name</S.Label>
              {isEditing && isAdmin ? (
                <S.Input
                  value={question.questionName || ''}
                  onChange={(e) => updateQuestion(sectionType, questionIndex, 'questionName', e.target.value)}
                  placeholder="Enter question name (optional)..."
                />
              ) : (
                <div style={{ padding: '10px', border: '1px solid #e9ecef', borderRadius: '4px', background: '#f8f9fa' }}>
                  {question.questionName || 'Not specified'}
                </div>
              )}
            </S.FormGroup>
          </S.Col>
        </S.Row>
        <S.Row>
          <S.Col>
            <S.FormGroup>
              <S.Label>Question Description</S.Label>
              {isEditing && isAdmin ? (
                <S.TextArea
                  value={question.questionDescription || ''}
                  onChange={(e) => updateQuestion(sectionType, questionIndex, 'questionDescription', e.target.value)}
                  placeholder="Enter question description (optional)..."
                  style={{ minHeight: '80px' }}
                />
              ) : (
                <div style={{ padding: '10px', border: '1px solid #e9ecef', borderRadius: '4px', background: '#f8f9fa', minHeight: '80px' }}>
                  {question.questionDescription || 'No description provided'}
                </div>
              )}
            </S.FormGroup>
          </S.Col>
        </S.Row>
      </>
    );
  };  // 计算模板总分
  const calculateTotalScore = () => {
    let totalScore = 0;
    Object.values(templateData.sections).forEach(section => {
      section.questions.forEach(question => {
        const score = question.score;
        if (score && !isNaN(score) && score > 0) {
          totalScore += score;
        }
      });
    });
    return totalScore;
  };

  // 计算各部分得分分布
  const calculateSectionScores = () => {
    const sectionScores = {};
    Object.entries(templateData.sections).forEach(([sectionName, section]) => {
      let sectionScore = 0;
      let validQuestionCount = 0;
      section.questions.forEach(question => {
        const score = question.score;
        if (score && !isNaN(score) && score > 0) {
          sectionScore += score;
          validQuestionCount++;
        }
      });
      sectionScores[sectionName] = {
        score: sectionScore,
        questionCount: section.questions.length,
        validQuestionCount: validQuestionCount
      };
    });
    return sectionScores;
  };

  return (
    <S.Container>
      {isLoading && (
        <div style={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          background: 'rgba(0,0,0,0.5)', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          zIndex: 1000,
          color: 'white',
          fontSize: '18px'
        }}>
          {bulkUpdateProgress ? (
            <div style={{ textAlign: 'center' }}>
              <div style={{ marginBottom: '10px' }}>
                {bulkUpdateProgress.phase === 'updating_template' && 'Updating template section name...'}
                {bulkUpdateProgress.phase === 'preparing' && 'Preparing to update student responses...'}
                {bulkUpdateProgress.phase === 'updating' && `Updating student responses... ${bulkUpdateProgress.current}/${bulkUpdateProgress.total}`}
                {bulkUpdateProgress.phase === 'saving_template_final' && 'Saving updated template...'}
                {bulkUpdateProgress.phase === 'completed' && `Successfully updated ${bulkUpdateProgress.current} responses`}
                {bulkUpdateProgress.phase === 'rolling_back' && 'Rolling back changes...'}
                {bulkUpdateProgress.phase === 'failed' && 'Update failed - rolling back changes'}
              </div>
              {bulkUpdateProgress.total > 0 && (
                <div style={{ 
                  width: '300px', 
                  height: '20px', 
                  background: '#333', 
                  borderRadius: '10px', 
                  overflow: 'hidden',
                  margin: '0 auto'
                }}>
                  <div style={{ 
                    width: `${(bulkUpdateProgress.current / bulkUpdateProgress.total) * 100}%`, 
                    height: '100%', 
                    background: bulkUpdateProgress.phase === 'failed' ? '#dc3545' : '#28a745',
                    transition: 'width 0.3s ease'
                  }} />
                </div>
              )}
              {bulkUpdateProgress.error && (
                <div style={{ color: '#dc3545', marginTop: '10px', fontSize: '14px' }}>
                  Error: {bulkUpdateProgress.error}
                </div>
              )}
            </div>
          ) : (
            'Loading...'
          )}
        </div>
      )}

      {/* Navigation Sidebar */}
      {showNavigation && (
        <S.NavigationSidebar>
          <S.NavigationHeader>
            <h4>Navigation</h4>
            <S.ToggleButton onClick={() => setShowNavigation(false)}>
              ←
            </S.ToggleButton>
          </S.NavigationHeader>          <S.NavigationContent>            <S.NavigationItem onClick={() => scrollToSection('section-basic-info')}>
              📋 Basic Information
            </S.NavigationItem>
            {Object.entries(templateData.sections).map(([sectionType, section]) => (
              <S.NavigationSection key={sectionType}>                <S.NavigationItem 
                  onClick={() => scrollToSection(`section-${sectionType}`)}
                  isSection
                >
                  📁 {sectionType} ({section.questionCount})
                </S.NavigationItem>
                {section.questions.map((question, index) => (                  <S.NavigationSubItem 
                    key={index}
                    onClick={() => scrollToSection(`section-${sectionType}-question-${index}`)}
                  >
                    📝 Q{question.questionNumber || (index + 1)} {question.questionName && `- ${question.questionName}`}
                  </S.NavigationSubItem>
                ))}
              </S.NavigationSection>
            ))}
            {/* 添加 Manage Sections 导航项（仅适用于 Final Exam 且在编辑模式下） */}
            {templateType === 'finalExam' && isEditing && isAdmin && (
              <S.NavigationItem onClick={() => scrollToSection('section-manage-sections')}>
                ⚙️ Manage Sections
              </S.NavigationItem>
            )}
          </S.NavigationContent>
        </S.NavigationSidebar>
      )}

      {/* Toggle Navigation Button */}
      {!showNavigation && (
        <S.ShowNavigationButton onClick={() => setShowNavigation(true)}>
          →
        </S.ShowNavigationButton>
      )}

      <S.MainContent showNavigation={showNavigation}>
        <S.Header>
          <div>
            <S.Title>Test Template Editor</S.Title>
            {
              isExistingTemplate && (
                  <S.TemplateId>ID: {templateId}</S.TemplateId>
              )
            }
          </div>
        </S.Header>

        <S.FloatingButtonGroup>
          <S.Button secondary onClick={goBack} disabled={isLoading}>
            Back to Dashboard
          </S.Button>
          {isAdmin && (
            <S.Button primary onClick={toggleEdit} disabled={isLoading}>
              {isEditing ? 'View Mode' : 'Edit Mode'}
            </S.Button>
          )}
          {isAdmin && (
            <S.Button success onClick={saveTemplate} disabled={isLoading}>
              {isLoading ? 'Saving...' : (isExistingTemplate ? 'Update Template' : 'Save Template')}
            </S.Button>
          )}
        </S.FloatingButtonGroup>

        {/* 基本信息 */}
        <S.FormSection id="section-basic-info">
          <S.SectionTitle>Basic Information</S.SectionTitle>
          <S.Row>
            <S.Col>
              <S.FormGroup>
                <S.Label>Template Type</S.Label>
                {isEditing && isAdmin && !isExistingTemplate ? (
                  <ChipSelect
                    aria-label="Template type"
                    options={TEMPLATE_TYPE_OPTIONS}
                    value={templateType}
                    onChange={handleTemplateTypeChange}
                  />
                ) : (
                  <div>{templateType === 'mockTest' ? 'Mock Test' : 'Final Exam'}</div>
                )}
              </S.FormGroup>
            </S.Col>
            <S.Col>
              <S.FormGroup>
                <S.Label>Exam Name</S.Label>
                {isEditing && isAdmin ? (
                  <S.Input
                    value={templateData.examName}
                    onChange={(e) => updateBasicInfo('examName', e.target.value)}
                    placeholder="Enter exam name"
                  />
                ) : (
                  <div>{templateData.examName || 'Not specified'}</div>
                )}
              </S.FormGroup>
            </S.Col>
          </S.Row>
          <S.Row>
            <S.Col>
              <S.FormGroup>
                <S.Label>Exam Date</S.Label>
                {isEditing && isAdmin ? (
                  <S.Input
                    type="datetime-local"
                    value={templateData.examDate}
                    onChange={(e) => updateBasicInfo('examDate', e.target.value)}
                  />
                ) : (
                  <div>{templateData.examDate || 'Not specified'}</div>
                )}
              </S.FormGroup>
            </S.Col>
            <S.Col>
              <S.FormGroup>
                <S.Label>Term</S.Label>
                {isEditing && isAdmin ? (
                  <ChipSelect
                    aria-label="Term"
                    allowDeselect
                    options={TERM_OPTIONS}
                    value={templateTerm || ''}
                    onChange={(term) => setTemplateTerm(term === '' ? null : Number(term))}
                  />
                ) : (
                  <div>{templateTerm ? `Term ${templateTerm}` : 'Not specified'}</div>
                )}
              </S.FormGroup>
            </S.Col>
          </S.Row>

          {/* Custom Report Assets Section - For existing templates in edit mode */}
          {isExistingTemplate && isEditing && isAdmin && (
            <>
              <S.Row style={{ marginTop: '20px' }}>
                <S.Col>
                  <S.FormGroup>
                    <S.Label>Custom Cover Image (Optional)</S.Label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {coverImageUrl ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                          <img
                            src={coverImageUrl}
                            alt="Cover preview"
                            style={{
                              width: '100px',
                              height: '140px',
                              objectFit: 'cover',
                              border: '1px solid #ddd',
                              borderRadius: '4px'
                            }}
                          />
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <span style={{ fontSize: '12px', color: '#666' }}>Custom cover image is set</span>
                            <S.Button
                              danger
                              onClick={removeCoverImage}
                              style={{ padding: '5px 10px', fontSize: '12px' }}
                              disabled={isLoading}
                            >
                              Remove Cover Image
                            </S.Button>
                          </div>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <input
                            type="file"
                            accept="image/jpeg,image/png,image/jpg"
                            onChange={handleCoverImageUpload}
                            disabled={isUploadingCover}
                            style={{ display: 'none' }}
                            id="cover-image-upload"
                          />
                          <label htmlFor="cover-image-upload">
                            <S.Button
                              as="span"
                              primary
                              style={{ cursor: isUploadingCover ? 'wait' : 'pointer' }}
                            >
                              {isUploadingCover ? 'Uploading...' : 'Upload Cover Image'}
                            </S.Button>
                          </label>
                          <span style={{ fontSize: '12px', color: '#666' }}>JPEG or PNG, max 5MB. Replaces default cover.</span>
                        </div>
                      )}
                    </div>
                  </S.FormGroup>
                </S.Col>
              </S.Row>
              <S.Row>
                <S.Col>
                  <S.FormGroup>
                    <S.Label>Exam Advice PDF (Optional)</S.Label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {examAdvicePdfUrl ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                          <div style={{
                            width: '60px',
                            height: '80px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: '#f5f5f5',
                            fontSize: '24px'
                          }}>
                            📄
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <a
                              href={examAdvicePdfUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ fontSize: '12px', color: '#007bff' }}
                            >
                              View Exam Advice PDF
                            </a>
                            <S.Button
                              danger
                              onClick={removeExamAdvicePdf}
                              style={{ padding: '5px 10px', fontSize: '12px' }}
                              disabled={isLoading}
                            >
                              Remove Exam Advice PDF
                            </S.Button>
                          </div>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <input
                            type="file"
                            accept="application/pdf"
                            onChange={handleExamAdvicePdfUpload}
                            disabled={isUploadingAdvicePdf}
                            style={{ display: 'none' }}
                            id="exam-advice-pdf-upload"
                          />
                          <label htmlFor="exam-advice-pdf-upload">
                            <S.Button
                              as="span"
                              primary
                              style={{ cursor: isUploadingAdvicePdf ? 'wait' : 'pointer' }}
                            >
                              {isUploadingAdvicePdf ? 'Uploading...' : 'Upload Exam Advice PDF'}
                            </S.Button>
                          </label>
                          <span style={{ fontSize: '12px', color: '#666' }}>PDF, max 10MB. Inserted after cover page.</span>
                        </div>
                      )}
                    </div>
                  </S.FormGroup>
                </S.Col>
              </S.Row>
            </>
          )}

          <S.FormGroup>
            <S.Label>Exam Advice</S.Label>
            {isEditing && isAdmin ? (
              <S.TextArea
                value={templateData.examAdvice}
                onChange={(e) => updateBasicInfo('examAdvice', e.target.value)}
                placeholder="Enter general advice for the exam"
              />
            ) : (
              <div>{templateData.examAdvice || 'No advice provided'}</div>
            )}
          </S.FormGroup>
          <S.FormGroup>
            <S.Label>Exam Advice Chinese</S.Label>
            {isEditing && isAdmin ? (
              <S.TextArea
                value={templateData.examAdviceCN || ''}
                onChange={(e) => updateBasicInfo('examAdviceCN', e.target.value)}
                placeholder="Enter general advice for the exam in Chinese"
              />
            ) : (
              <div>{templateData.examAdviceCN || 'No advice provided'}</div>
            )}
          </S.FormGroup>
        </S.FormSection>        {/* 各个部分 */}
        {Object.entries(templateData.sections).map(([sectionType, section], sectionIndex, sectionEntries) => (
          <S.FormSection key={sectionType} id={`section-${sectionType}`}>
            <S.CollapsibleSectionHeader>
              {/* Section Title - Editable for Final Exam, Fixed for Mock Test */}
              {templateType === 'finalExam' && isEditing && isAdmin && editingSectionName === sectionType ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1 }}>
                  <S.Input
                    value={editingSectionNewName}
                    onChange={(e) => setEditingSectionNewName(e.target.value)}
                    placeholder="Enter section name..."
                    style={{ flex: 1, maxWidth: '300px' }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        saveSectionNameChange();
                      } else if (e.key === 'Escape') {
                        e.preventDefault();
                        cancelEditingSectionName();
                      }
                    }}
                    autoFocus
                  />
                  <S.Button 
                    success 
                    onClick={saveSectionNameChange}
                    style={{ padding: '5px 10px', fontSize: '12px' }}
                  >
                    Save and Update Template
                  </S.Button>
                  <S.Button 
                    secondary 
                    onClick={cancelEditingSectionName}
                    style={{ padding: '5px 10px', fontSize: '12px' }}
                  >
                    Cancel
                  </S.Button>
                </div>
              ) : (
                <>
                  <S.SectionTitle onClick={() => toggleSectionCollapse(sectionType)}>
                    <S.CollapseIcon collapsed={collapsedSections[sectionType]}>
                      ▼
                    </S.CollapseIcon>
                    {sectionType} Section
                  </S.SectionTitle>
                  
                  {/* 上移/下移 section 按钮（仅适用于Final Exam且在编辑模式下） */}
                  {templateType === 'finalExam' && isEditing && isAdmin && (
                    <>
                      <S.Button
                        secondary
                        onClick={(e) => {
                          e.stopPropagation();
                          moveSectionUp(sectionType);
                        }}
                        disabled={sectionIndex === 0}
                        style={{ marginLeft: '10px', padding: '5px 10px', fontSize: '12px' }}
                        title="Move section up"
                      >
                        ▲ Up
                      </S.Button>
                      <S.Button
                        secondary
                        onClick={(e) => {
                          e.stopPropagation();
                          moveSectionDown(sectionType);
                        }}
                        disabled={sectionIndex === sectionEntries.length - 1}
                        style={{ marginLeft: '5px', padding: '5px 10px', fontSize: '12px' }}
                        title="Move section down"
                      >
                        ▼ Down
                      </S.Button>
                    </>
                  )}

                  {/* 添加编辑section名称按钮（仅适用于Final Exam且在编辑模式下） */}
                  {templateType === 'finalExam' && isEditing && isAdmin && (
                    <S.Button
                      primary
                      onClick={(e) => {
                        e.stopPropagation();
                        startEditingSectionName(sectionType);
                      }}
                      style={{ marginLeft: '10px', padding: '5px 10px', fontSize: '12px' }}
                    >
                      Edit Name
                    </S.Button>
                  )}

                  {/* 添加删除section按钮（仅适用于Final Exam且在编辑模式下） */}
                  {templateType === 'finalExam' && isEditing && isAdmin && (
                    <S.Button 
                      danger 
                      onClick={(e) => {
                        e.stopPropagation();
                        removeSection(sectionType);
                      }}
                      style={{ marginLeft: '10px', padding: '5px 10px', fontSize: '12px' }}
                    >
                      Delete Section
                    </S.Button>
                  )}
                </>
              )}
              
              {/* 为Mock Test显示固定section提示 */}
              {templateType === 'mockTest' && (
                <div style={{ 
                  marginLeft: '10px', 
                  padding: '5px 10px', 
                  fontSize: '12px', 
                  color: '#6c757d',
                  fontStyle: 'italic'
                }}>
                  Fixed Section (Mock Test)
                </div>
              )}
            </S.CollapsibleSectionHeader>
            
            {!collapsedSections[sectionType] && (
              <>
                <S.Row>
                  <S.Col>
                    <S.FormGroup>
                      <S.Label>Question Count</S.Label>
                      <div>{section.questionCount}</div>
                    </S.FormGroup>
                  </S.Col>
                  <S.Col>
                    {/* Remove difficulty level for finalExam */}
                    {templateType === 'mockTest' && (
                      <S.FormGroup>
                        <S.Label>Difficulty Level</S.Label>
                        {isEditing && isAdmin ? (
                          <S.Input
                            type="number"
                            min="1"
                            step="1"
                            value={section.difficulty}
                            onChange={(e) => {
                              const value = parseInt(e.target.value);
                              if (value > 0 && !isNaN(value)) {
                                updateSection(sectionType, 'difficulty', value);
                              }
                            }}
                          />
                        ) : (
                          <div>{section.difficulty}</div>
                        )}
                      </S.FormGroup>
                    )}
                  </S.Col>
                </S.Row>

                <S.FormGroup>
                  <S.Label>Section Advice</S.Label>
                  {isEditing && isAdmin ? (
                    <S.TextArea
                      value={section.sectionAdvice}
                      onChange={(e) => updateSection(sectionType, 'sectionAdvice', e.target.value)}
                      placeholder={`Enter advice for ${sectionType} section`}
                    />
                  ) : (
                    <div>{section.sectionAdvice || 'No advice provided'}</div>
                  )}
                </S.FormGroup>
                <S.FormGroup>
                  <S.Label>Section Advice Chinese</S.Label>
                  {isEditing && isAdmin ? (
                    <S.TextArea
                      value={section.sectionAdviceCN || ''}
                      onChange={(e) => updateSection(sectionType, 'sectionAdviceCN', e.target.value)}
                      placeholder={`Enter advice for ${sectionType} section in Chinese`}
                    />
                  ) : (
                    <div>{section.sectionAdviceCN || 'No advice provided'}</div>
                  )}
                </S.FormGroup>                {/* 题目列表 */}
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                    <h3>Questions</h3>
                    {isEditing && isAdmin && section.questions.length > 0 && (
                      <S.Button 
                        danger 
                        onClick={() => clearSectionQuestions(sectionType)}
                        style={{ 
                          padding: '6px 12px', 
                          fontSize: '12px',
                          background: '#dc3545',
                          color: 'white'
                        }}
                      >
                        Clear All Questions
                      </S.Button>
                    )}
                  </div>
                  {section.questions.map((question, index) => (
                    <S.QuestionContainer key={index} id={`section-${sectionType}-question-${index}`}>
                      <S.QuestionHeader>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <span>Position {index + 1} - Question {question.questionNumber || `#${index + 1}`}</span>
                          <span style={{ fontSize: '12px', color: '#6c757d' }}>(ID: {question.questionNumber})</span>
                        </div>
                        {isEditing && isAdmin && (
                          <div style={{ display: 'flex', gap: '5px' }}>
                            <S.Button
                              primary
                              onClick={() => moveQuestionUp(sectionType, index)}
                              disabled={index === 0}
                              style={{
                                padding: '4px 8px',
                                fontSize: '12px',
                                minWidth: 'auto',
                                opacity: index === 0 ? 0.5 : 1
                              }}
                              title="Move question up"
                            >
                              ↑
                            </S.Button>
                            <S.Button
                              primary
                              onClick={() => moveQuestionDown(sectionType, index)}
                              disabled={index === section.questions.length - 1}
                              style={{
                                padding: '4px 8px',
                                fontSize: '12px',
                                minWidth: 'auto',
                                opacity: index === section.questions.length - 1 ? 0.5 : 1
                              }}
                              title="Move question down"
                            >
                              ↓
                            </S.Button>
                            <S.RemoveButton
                              danger
                              onClick={() => removeQuestion(sectionType, index)}
                              style={{ padding: '4px 8px', fontSize: '12px' }}
                            >
                              Remove
                            </S.RemoveButton>
                          </div>
                        )}
                      </S.QuestionHeader>
                      <S.QuestionContent>
                        <S.Row>
                          <S.Col flex="3">
                            {renderQuestionMeta(sectionType, question, index)}
                          </S.Col>
                          <S.Col flex="1">
                            <S.FormGroup>
                              <S.Label>Score</S.Label>                              {isEditing && isAdmin ? (
                                <S.Input
                                  type="number"
                                  min="0.1"
                                  step="0.1"
                                  value={question.score || ''}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    const numValue = parseFloat(value);
                                    
                                    // Update the question score (allow empty or any value)
                                    updateQuestion(sectionType, index, 'score', value === '' ? '' : numValue);
                                    
                                    // Track invalid questions for visual feedback
                                    const questionKey = `${sectionType}-${index}`;
                                    setInvalidQuestions(prev => {
                                      const newInvalid = { ...prev };
                                      if (value === '' || numValue <= 0 || isNaN(numValue)) {
                                        newInvalid[questionKey] = true;
                                      } else {
                                        delete newInvalid[questionKey];
                                      }
                                      return newInvalid;
                                    });
                                  }}
                                  style={{
                                    borderColor: invalidQuestions[`${sectionType}-${index}`] ? '#dc3545' : undefined,
                                    backgroundColor: invalidQuestions[`${sectionType}-${index}`] ? '#fff5f5' : undefined
                                  }}
                                  placeholder="Enter score..."
                                />
                              ) : (
                                <div>{question.score}</div>
                              )}
                              {/* Show error message for invalid scores */}
                              {isEditing && isAdmin && invalidQuestions[`${sectionType}-${index}`] && (
                                <div style={{ 
                                  color: '#dc3545', 
                                  fontSize: '12px', 
                                  marginTop: '4px' 
                                }}>
                                  Score must be greater than 0
                                </div>
                              )}
                            </S.FormGroup>
                          </S.Col>
                        </S.Row>
                      </S.QuestionContent>
                    </S.QuestionContainer>
                  ))}                    {isEditing && isAdmin && (
                    <div>
                      <S.Row style={{ marginBottom: '10px' }}>
                        <S.Col>
                          <S.Label>Question Type for New Question</S.Label>
                          <ChipSelect
                            aria-label="Question type for new questions"
                            options={QUESTION_TYPE_OPTIONS}
                            value={selectedQuestionType[sectionType] || 'Objective'}
                            onChange={(questionType) => setSelectedQuestionType(prev => ({
                              ...prev,
                              [sectionType]: questionType
                            }))}
                          />
                        </S.Col>
                        <S.Col>
                          <S.Label>Number of Questions to Add</S.Label>
                          <S.Input
                            type="number"
                            min="1"
                            max="50"
                            value={batchAddCount[sectionType] || 1}
                            onChange={(e) => setBatchAddCount(prev => ({
                              ...prev,
                              [sectionType]: parseInt(e.target.value) || 1
                            }))}
                            placeholder="1"
                            style={{ width: '100px' }}
                          />
                        </S.Col>
                        <S.Col>
                          <S.Label>Score for Each Question</S.Label>
                          <S.Input
                            type="number"
                            min="0.1"
                            step="0.1"
                            value={batchAddScore[sectionType] !== undefined ? batchAddScore[sectionType] : 1}
                            onChange={(e) => {
                              const value = e.target.value;
                              const numValue = parseFloat(value);
                              setBatchAddScore(prev => ({
                                ...prev,
                                [sectionType]: value === '' ? '' : numValue
                              }));
                            }}
                            placeholder="1"
                            style={{ width: '100px' }}
                          />
                        </S.Col>
                      </S.Row><S.AddButton onClick={() => handleAddQuestion(sectionType)}>
                        Add {(batchAddCount[sectionType] || 1) > 1 ? `${batchAddCount[sectionType] || 1} Questions` : 'Question'} to {sectionType}
                      </S.AddButton>
                    </div>
                  )}
                </div>
              </>
            )}          </S.FormSection>
        ))}        {/* Section Management for Final Exam */}
        {templateType === 'finalExam' && isEditing && isAdmin && (
          <S.FormSection id="section-manage-sections">
            <S.SectionTitle>Manage Sections</S.SectionTitle>
            
            {/* 快速添加预设sections */}
            <S.FormGroup>
              <S.Label>Add Common Sections</S.Label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '20px' }}>
                {defaultFinalExamSections.map(sectionName => {
                  const alreadyExists = templateData.sections.hasOwnProperty(sectionName);
                  return (
                    <S.Button
                      key={sectionName}
                      primary={!alreadyExists}
                      secondary={alreadyExists}
                      disabled={alreadyExists}
                      onClick={() => !alreadyExists && addPresetSection(sectionName)}
                      style={{ 
                        minWidth: 'auto', 
                        padding: '8px 12px',
                        opacity: alreadyExists ? 0.5 : 1
                      }}
                    >
                      {alreadyExists ? '✓ ' : '+ '}{sectionName}
                    </S.Button>
                  );
                })}
              </div>
            </S.FormGroup>

            {/* 添加自定义section */}
            <S.FormGroup>
              <S.Label>Add Custom Section</S.Label>
              {!showAddSection ? (
                <S.Button 
                  primary 
                  onClick={() => setShowAddSection(true)}
                  style={{ minWidth: 'auto' }}
                >
                  + Add Custom Section
                </S.Button>
              ) : (
                <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
                  <div style={{ flex: 1 }}>
                    <S.Input
                      value={newSectionName}
                      onChange={(e) => setNewSectionName(e.target.value)}
                      placeholder="Enter section name..."
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          addSection(newSectionName);
                        }
                      }}
                    />
                  </div>
                  <S.Button 
                    success 
                    onClick={() => addSection(newSectionName)}
                    style={{ minWidth: 'auto', padding: '10px 16px' }}
                  >
                    Add
                  </S.Button>
                  <S.Button 
                    secondary 
                    onClick={() => {
                      setShowAddSection(false);
                      setNewSectionName('');
                    }}
                    style={{ minWidth: 'auto', padding: '10px 16px' }}
                  >
                    Cancel
                  </S.Button>
                </div>
              )}
            </S.FormGroup>

            {Object.keys(templateData.sections).length === 0 && (
              <div style={{ 
                textAlign: 'center', 
                padding: '20px', 
                background: '#f8f9fa', 
                borderRadius: '4px',
                color: '#6c757d',
                marginTop: '20px'
              }}>
                <p>No sections added yet. Add sections above to start creating your exam template.</p>
              </div>
            )}
          </S.FormSection>
        )}
      </S.MainContent>
    </S.Container>
  );
};

export default TestTemplate;